import React from "react";
import { Link } from "react-router-dom";
import { Card, CardBody } from "reactstrap";
import * as moment from "moment";
import Permissions from "Components/Common/Permissions";
import { PSEA_UPDATE_CHECKS } from "./usePseaPermissions";
import {
  PSEA_TOTAL_STANDARDS,
  PseaAnswer,
  pseaRatingColor,
} from "./pseaScoring";
import { PSEAStandardCard, PSEAStandardGrid } from "./PSEAStandardCard";

type StandardEntry = { standard: any; answer: PseaAnswer };

const formatDate = (value: any) =>
  value ? moment(new Date(value)).format("DD MMM YYYY") : "Not set";

const EditButton = ({ id }: { id: any }) => (
  <Permissions checks={PSEA_UPDATE_CHECKS}>
    <Link
      to={`/assessments/psea-assessments/${id}/edit`}
      className="btn btn-soft-primary d-inline-flex align-items-center gap-1"
    >
      <i className="ri-pencil-line align-bottom"></i>
      Edit Assessment
    </Link>
  </Permissions>
);

/**
 * Score gauge; the filled arc is the share of standards met. `color` is the
 * rating band's colour so the gauge and the rating beside it read as one.
 */
const ScoreDonut = ({
  score,
  color,
  size = 104,
  recorded = true,
}: {
  score: number;
  color: string;
  size?: number;
  /**
   * False for legacy assessments imported as a rating alone. They have no core
   * standards at all, so a "0 of 8" gauge would read as every standard failed
   * rather than as nothing recorded — the arc is left empty and the figure
   * replaced with a dash.
   */
  recorded?: boolean;
}) => {
  const radius = size * 0.423;
  const circumference = 2 * Math.PI * radius;
  const filled = recorded ? (score / PSEA_TOTAL_STANDARDS) * circumference : 0;
  const c = size / 2;

  return (
    <div
      className="position-relative flex-shrink-0"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={c}
          cy={c}
          r={radius}
          fill="none"
          stroke="var(--vz-gray-200)"
          strokeWidth="8"
        />
        <circle
          cx={c}
          cy={c}
          r={radius}
          fill="none"
          stroke={`var(--vz-${color})`}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circumference}`}
          transform={`rotate(-90 ${c} ${c})`}
        />
      </svg>
      <div className="position-absolute top-50 start-50 translate-middle text-center lh-1">
        <div className={`fs-24 fw-semibold text-${recorded ? color : "muted"}`}>
          {recorded ? score : "—"}
        </div>
        <div className="text-muted fs-11">
          {recorded ? `of ${PSEA_TOTAL_STANDARDS}` : "not scored"}
        </div>
      </div>
    </div>
  );
};

/**
 * Summary header for a PSEA assessment: the outcome up top, the per-standard
 * breakdown beneath it in the same card. Cards link to the matching standard
 * further down the page.
 *
 * `embedded` is the contractor-page variant. The contractor is already named by
 * the page header there, so the rating leads instead of repeating it.
 */
const PSEAAssessmentHeader = ({
  data,
  standards,
  onSelect,
  embedded,
}: {
  data: any;
  standards: StandardEntry[];
  onSelect: (standardNumber: number) => void;
  embedded?: boolean;
}) => {
  const score = standards.filter(
    (s) => s.answer === "yes" || s.answer === "na"
  ).length;
  const unanswered = standards.filter((s) => s.answer === null).length;

  // Legacy assessments imported as a rating alone hold no core standards at
  // all, which is different from an assessment whose standards are merely
  // unanswered — there is nothing to score rather than nothing answered yet.
  const hasCoreStandards = (data?.coreStandards || []).length > 0;

  // The rating drives its own colour, so the band reads at a glance:
  // Full = success, Medium = warning, Low = danger, unrated = secondary. Taken
  // from the saved rating rather than the live score so it always matches the
  // text beside it, even on records predating automatic scoring.
  const ratingColor = pseaRatingColor(data?.overallRating);

  return (
    <Card className="overflow-hidden">
      {/* Outcome */}
      <CardBody className="pb-4">
        <div className="d-flex align-items-center gap-4 flex-wrap">
          <ScoreDonut
            score={score}
            color={ratingColor}
            recorded={hasCoreStandards}
          />

          <div className="flex-grow-1" style={{ minWidth: "220px" }}>
            <p className="text-uppercase text-muted fw-medium fs-11 mb-1">
              {embedded ? "Overall PSEA Rating" : "Contractor Assessed"}
            </p>

            {embedded ? (
              <h4 className={`mb-2 text-${ratingColor}`}>
                {data?.overallRating || "Not rated"}
              </h4>
            ) : (
              <React.Fragment>
                <h4 className="mb-2 text-truncate">
                  <Link
                    to={`/contractors/${data?.contractor?.id}`}
                    className="text-body"
                  >
                    {data?.contractor?.contractorName || "-"}
                  </Link>
                </h4>
                <div className="mb-2">
                  <span
                    className={`badge bg-${ratingColor}-subtle text-${ratingColor} fs-13 px-3 py-2`}
                  >
                    {data?.overallRating || "Not rated"}
                  </span>
                </div>
              </React.Fragment>
            )}

            <div className="d-flex align-items-center gap-3 flex-wrap text-muted fs-13">
              <span>
                <i className="ri-calendar-line align-bottom me-1"></i>
                {formatDate(data?.dateOfAssessment)}
              </span>
              {data?.agency?.name ? (
                <span>
                  <i className="ri-earth-line align-bottom me-1"></i>
                  {data.agency.name}
                </span>
              ) : null}
            </div>
          </div>

          <div className="align-self-start">
            <EditButton id={data.id} />
          </div>
        </div>
      </CardBody>

      {/* Per-standard breakdown */}
      <div className="border-top bg-light-subtle p-3">
        <div className="d-flex align-items-baseline justify-content-between flex-wrap gap-2 mb-2">
          <span className="text-uppercase text-muted fw-medium fs-11">
            Core standards
          </span>
          <span className="text-muted fs-12">
            {!hasCoreStandards
              ? "not recorded for this assessment"
              : `${unanswered > 0 ? `${unanswered} unanswered · ` : ""}click a standard to open it`}
          </span>
        </div>

        <PSEAStandardGrid>
          {standards.map(({ standard, answer }) => (
            <PSEAStandardCard
              key={standard.number}
              standard={standard}
              answer={answer}
              onSelect={onSelect}
            />
          ))}
        </PSEAStandardGrid>
      </div>
    </Card>
  );
};

export default PSEAAssessmentHeader;
